import { arrayOf, bool, number, oneOf, shape, string } from 'prop-types'

import { OrderStatus } from 'views/components/organisms/OrderDetailsContent/OrderStatus/const'

export const statusType = oneOf([
  OrderStatus.PROCESSING,
  OrderStatus.WAITING_FOR_PREPAYMENT,
  OrderStatus.NEW,
  OrderStatus.CANCELED,
  OrderStatus.CLOSED,
  OrderStatus.DISPATCHED,
])

export const cancellationStatusType = oneOf([
  'rejected',
  'accepted',
  'requested',
])

export const medicationType = oneOf([
  'pzn',
  'composite',
  'activeSubstance',
  'freetext',
])

export const documentPositionType = shape({
  pzn: string.isRequired,
  itemName: string.isRequired,
  quantity: number.isRequired,
  packagingQuantity: number.isRequired,
  packagingUnit: string.isRequired,
  salesPrice: number.isRequired,
  pricePerUnit: shape({
    formatted: string.isRequired,
  }),
  availability: string,
  redPoints: number,
  thumbnailURL: string.isRequired,
  canonicalURL: string,
  lineDiscountPercent: number,
})

export const documentERxPositionType = shape({
  availability: string.isRequired,
  lineNum: number.isRequired,
  pzn: string,
  itemName: string.isRequired,
  quantity: number.isRequired,
  salesPrice: number.isRequired,
  lineAmount: number.isRequired,
  packagingQuantity: number.isRequired,
  pharmaForm: string.isRequired,
  thumbnailURL: string.isRequired,
  medicationType: medicationType.isRequired,
  prescriptionIssuedTo: string.isRequired,
  prescriptionDate: string.isRequired,
  hashedToken: string.isRequired,
  copaymentFree: bool.isRequired,
})

export const orderDocumentsType = arrayOf(
  shape({
    type: string.isRequired,
    documentPositions: arrayOf(documentPositionType),
  }).isRequired
)

export const orderDocumentsERxType = arrayOf(
  shape({
    type: string.isRequired,
    documentPositions: arrayOf(documentERxPositionType),
  }).isRequired
)

export const paymentType = shape({
  paymentKind: string.isRequired,
  paymentProviderTransactionId: string.isRequired,
  paymentStatus: string.isRequired,
})

export const paymentInformation = shape({
  paymentInformation: shape({
    bankAccountHolder: string,
    bankName: string,
    bic: string,
    iban: string,
    paymentReference: string,
  }),
})

export const addressType = shape({
  id: string,
  salutation: string,
  firstName: string,
  lastName: string,
  street: string.isRequired,
  streetNo: string,
  letterBox: string,
  extension: string,
  city: string.isRequired,
  zip: string.isRequired,
  countryCode: string,
  pickupStation: shape({
    name: string,
  }),
  doorCode: string,
  taxNumber: string,
  type: string,
})

export const billingAddressType = shape({
  id: string,
  street: string.isRequired,
  streetNo: string,
  letterBox: string,
  extension: string,
  city: string.isRequired,
  zip: string.isRequired,
  countryCode: string,
  doorCode: string,
  type: oneOf(['billing', 'pickupStation', 'shipping']).isRequired,
})

export const deliveryAddressType = shape({
  id: string,
  salutation: string.isRequired,
  firstName: string.isRequired,
  lastName: string.isRequired,
  street: string.isRequired,
  streetNo: string,
  letterBox: string,
  extension: string,
  city: string.isRequired,
  zip: string.isRequired,
  countryCode: string,
  doorCode: string,
  type: oneOf(['billing', 'pickupStation', 'shipping']).isRequired,
})

export const deliveryType = shape({
  identifier: string.isRequired,
  costs: number.isRequired,
  displayName: string,
  trackingId: string,
})

export const subOrderType = shape({
  subOrderId: string.isRequired,
  sellerName: string.isRequired,
  status: statusType,
  redPoints: number,
  totalAmount: number.isRequired,
  payment: paymentType.isRequired,
  documentsToDownload: arrayOf(
    shape({
      'type': string.isRequired,
      'url': string.isRequired,
    })
  ),
  links: arrayOf(
    shape({
      'type': string.isRequired,
      'url': string.isRequired,
    })
  ),
  deliveryAddress: deliveryAddressType.isRequired,
  carrier: deliveryType.isRequired,
  orderDocuments: orderDocumentsType.isRequired,
  cancellable: bool,
  cancellationStatus: cancellationStatusType,
})

// app/shared/services/api/models/now/
const NowDataType = shape({
  merchant: shape({}).isRequired,
  slot: shape({}),
  slotPickup: shape({}),
})

export const subOrderTypeNew = shape({
  billingAddress: billingAddressType.isRequired,
  carrier: shape({
    identifier: string.isRequired,
    costs: number,
    trackingId: string,
    nowData: NowDataType,
  }),
  deliveryAddress: deliveryAddressType.isRequired,
  discounts: arrayOf(shape({})),
  documentsToDownload: arrayOf(
    shape({
      'type': string.isRequired,
      'url': string.isRequired,
    })
  ),
  links: arrayOf(
    shape({
      'type': string.isRequired,
      'url': string.isRequired,
    })
  ),
  orderDocuments: orderDocumentsType.isRequired,
  orderDocumentsERx: orderDocumentsERxType,
  orderType: string.isRequired,
  payment: paymentType.isRequired,
  redPoints: number,
  sellerId: string.isRequired,
  sellerName: string.isRequired,
  shippedDate: string,
  status: statusType,
  subOrderId: string.isRequired,
  totalAmount: number.isRequired,
  totalAmountDocuments: number.isRequired,
  totalAmountPrescriptionBonus: number.isRequired,
  cancellable: bool,
  cancellationStatus: cancellationStatusType,
  returnable: bool,
})

export const orderMasterDataType = shape({
  orderNumber: string.isRequired,
  orderDate: string.isRequired,
  totalAmount: number.isRequired,
  currency: string.isRequired,
})

export const customerMasterDataType = shape({
  customerNumber: string.isRequired,
  firstName: string.isRequired.isRequired,
  isEavCustomer: bool.isRequired,
  lastName: string.isRequired,
  mailingSubscriptions: arrayOf(shape({})),
  phone: string,
  isRedPointsMember: bool,
  salutation: string.isRequired,
  taxNumber: string,
})

export const orderType = shape({
  orderMasterData: orderMasterDataType.isRequired,
  subOrders: arrayOf(subOrderType).isRequired,
})

export const ordersListType = shape({
  page: number.isRequired,
  itemsPerPage: number.isRequired,
  orders: arrayOf(orderType).isRequired,
  totalItems: number.isRequired,
  totalPages: number.isRequired,
})

export const orderThreadSubjectsType = shape({
  reasons: arrayOf(
    shape({
      code: string.isRequired,
      label: string.isRequired,
    }).isRequired
  ).isRequired,
})
